import {
    ChatBubbleOutlineOutlined,
    WaterDropOutlined,
    WaterDrop,
    ShareOutlined,
  } from "@mui/icons-material";
  import { Box, Divider, IconButton, Typography, useTheme, InputBase, Button } from "@mui/material";
  import FlexBetween from "../../components/FlexBetween";
  import Friend from "../../components/Friend";
  import WidgetWrapper from "../../components/WidgetWrapper";
  import { useState, useRef, useEffect } from "react";
  import { useDispatch, useSelector } from "react-redux";
  import { setPost } from "../../state";
  import axios from "axios";
  
  const PostWidget = ({
    postId,
    postUserId,
    name,
    description,
    location,
    picturePath,
    userPicturePath,
    likes,
    comments,
  }) => {
    const [isComments, setIsComments] = useState(false);
    const [commentText, setCommentText] = useState("");
    const dispatch = useDispatch();
    const token = useSelector((state) => state.token);
    const loggedInUserId = useSelector((state) => state.user._id);
    const [localLikes, setLocalLikes] = useState(likes);
    const clickCountRef = useRef(0);
    const debounceTimeoutRef = useRef(null);

    // Sync local state if redux state changes
    useEffect(() => {
      setLocalLikes(likes);
    }, [likes]);

    const isLiked = Boolean(localLikes[loggedInUserId]);
    const likeCount = Object.values(localLikes).reduce((acc, curr) => {
      return acc + (typeof curr === 'boolean' ? 1 : Number(curr));
    }, 0);
  
    const { palette } = useTheme();
    const main = palette.neutral.main;
    const primary = palette.primary.main;
  
    const patchLike = () => {
      // Optimistic local update
      const currentLikes = typeof localLikes[loggedInUserId] === 'boolean' ? 1 : Number(localLikes[loggedInUserId] || 0);
      setLocalLikes({
        ...localLikes,
        [loggedInUserId]: currentLikes + 1
      });
      clickCountRef.current += 1;

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(async () => {
        const clicksToSend = clickCountRef.current;
        clickCountRef.current = 0; // reset for next batch

        if (clicksToSend === 0) return;

        try {
          const response = await axios.patch(
            `https://dropie-service.onrender.com/posts/${postId}/like`,
            { userId: loggedInUserId, clicks: clicksToSend },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
      
          const updatedPost = response.data;
          dispatch(setPost({ post: updatedPost }));
        } catch (error) {
          console.error('Error in patchLike:', error);
          setLocalLikes(likes); // Revert on error
        }
      }, 500);
    };

    const patchComment = async () => {
      try {
        const response = await axios.patch(
          `https://dropie-service.onrender.com/posts/${postId}/comment`,
          { comment: commentText, userId: loggedInUserId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        const updatedPost = response.data;
        dispatch(setPost({ post: updatedPost }));
        setCommentText("");
      } catch (error) {
        console.error('Error in patchComment:', error);
      }
    };

    const handleShare = async () => {
      try {
        const shareData = {
          title: `Post by ${name}`,
          text: description,
          url: window.location.origin + `/profile/${postUserId}`,
        };
        if (navigator.share) {
          await navigator.share(shareData);
        } else {
          await navigator.clipboard.writeText(shareData.url);
          alert("Link copied to clipboard!");
        }
      } catch (err) {
        console.error("Error sharing", err);
      }
    };
  
    return (
      <WidgetWrapper m="2rem 0">
        <Friend
          friendId={postUserId}
          name={name}
          subtitle={location}
          userPicturePath={userPicturePath}
        />
        <Typography color={main} sx={{ mt: "1rem" }}>
          {description}
        </Typography>
        {picturePath && (
          <img
            width="100%"
            height="auto"
            alt="post"
            style={{ borderRadius: "0.75rem", marginTop: "0.75rem" }}
            src={picturePath}
          />
        )}
        <FlexBetween mt="0.25rem">
          <FlexBetween gap="1rem">
            <FlexBetween gap="0.3rem">
              <IconButton onClick={patchLike}>
                {isLiked ? (
                  <WaterDrop sx={{ color: primary }} />
                ) : (
                  <WaterDropOutlined />
                )}
              </IconButton>
              <Typography>{likeCount}</Typography>
            </FlexBetween>
  
            <FlexBetween gap="0.3rem">
              <IconButton onClick={() => setIsComments(!isComments)}>
                <ChatBubbleOutlineOutlined />
              </IconButton>
              <Typography>{comments.length}</Typography>
            </FlexBetween>
          </FlexBetween>
  
          <IconButton onClick={handleShare}>
            <ShareOutlined />
          </IconButton>
        </FlexBetween>
        {isComments && (
          <Box mt="0.5rem">
            <Box display="flex" gap="0.5rem" mb="0.5rem" alignItems="center">
              <InputBase
                placeholder="Write a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                sx={{
                  width: "100%",
                  backgroundColor: palette.neutral.light,
                  borderRadius: "2rem",
                  padding: "0.5rem 1rem",
                }}
              />
              <Button
                disabled={!commentText}
                onClick={patchComment}
                sx={{
                  color: palette.background.alt,
                  backgroundColor: palette.primary.main,
                  borderRadius: "3rem",
                }}
              >
                POST
              </Button>
            </Box>
            {comments.map((comment, i) => {
              const isObj = typeof comment === "object" && comment !== null;
              const text = isObj ? comment.text : comment;
              const commenterName = isObj ? comment.name : "Anonymous";
              const picturePath = isObj ? comment.picturePath : "";

              return (
                <Box key={`${name}-${i}`}>
                  <Divider />
                  <Box sx={{ m: "0.5rem 0", pl: "1rem" }} display="flex" alignItems="center" gap="1rem">
                    {isObj ? (
                      picturePath ? (
                        <img src={picturePath} alt="user" style={{ width: "30px", height: "30px", borderRadius: "50%", objectFit: "cover" }} />
                      ) : (
                        <Box width="30px" height="30px" borderRadius="50%" backgroundColor={palette.neutral.light} />
                      )
                    ) : null}
                    <Box>
                      {isObj && <Typography variant="subtitle2" fontWeight="bold">{commenterName}</Typography>}
                      <Typography sx={{ color: main }}>{text}</Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            <Divider />
          </Box>
        )}
      </WidgetWrapper>
    );
  };
  
  
  export default PostWidget;
  
